import { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const CATEGORIES = ['General', 'Study Abroad', 'Work Placements', 'Payments', 'Visa Process'];

// Used only if the /faqs endpoint returns nothing yet.
const FALLBACK_FAQS = [
  {
    id: '1', category: 'General',
    question: 'How can I find scholarships to study abroad from Zimbabwe?',
    answer: 'Start by matching your grades, chosen course and destination with verified university and scholarship requirements. IASC Consultancy helps Zimbabwean students identify suitable study abroad and scholarship opportunities, then prepare the documents each application requires.',
  },
  {
    id: '2', category: 'Visa Process',
    question: 'What are the requirements for undergraduate scholarships in the USA from Zimbabwe?',
    answer: 'Requirements vary by scholarship and university, but commonly include academic transcripts, a valid passport, an application essay, recommendation letters and evidence of English proficiency where required. Always check the official provider criteria and deadline before applying.',
  },
  {
    id: '3', category: 'Study Abroad',
    question: 'Does IASC help students apply for scholarships to study abroad?',
    answer: 'Yes — we help identify eligible scholarships and assist with application requirements. Several of our study programs include partial or full tuition scholarship options.',
  },
  {
    id: '4', category: 'Payments',
    question: 'How can I write a strong scholarship essay for an overseas university?',
    answer: 'Use a clear, specific essay that connects your academic achievements, career goals and community impact to the scholarship purpose. Follow the prompt closely, give real examples and edit carefully for clarity before submission.',
  },
  {
    id: '5', category: 'Visa Process',
    question: 'Which countries offer fully funded scholarships for international students?',
    answer: 'Fully funded scholarship availability changes each year and depends on the course, institution and applicant profile. Countries across Europe, North America and Asia offer competitive funding; we can help you compare eligible options and official deadlines.',
  },
];

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [activeCategory, setActiveCategory] = useState('General');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/faqs`)
      .then((r) => r.json())
      .then((json) => setFaqs(json.success && json.data.length > 0 ? json.data : FALLBACK_FAQS))
      .catch(() => setFaqs(FALLBACK_FAQS));
  }, []);

  const filtered = faqs.filter((f) => {
    const matchesCategory = f.category === activeCategory;
    const matchesSearch = search === '' || f.question.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <Navbar />
      <main className="pt-20">
        <header className="pt-20 pb-20 bg-[#eceef0] text-center">
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-5xl font-bold text-[#00255a] mb-6">Study Abroad and Scholarship Questions</h1>
            <p className="text-lg text-[#44474f] mb-12">
              Clear answers about scholarships to study abroad, university applications, student visas and preparing for life overseas.
            </p>
            <div className="relative max-w-2xl mx-auto">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search scholarships, student visas or universities"
                className="w-full h-16 pl-14 pr-6 rounded-xl border border-[#e2e8f0] bg-white shadow-sm focus:ring-2 focus:ring-[#00255a]/20 focus:border-[#00255a] outline-none transition-all"
              />
              <span className="material-symbols-outlined absolute left-5 top-1/2 -translate-y-1/2 text-[#64748b]">search</span>
            </div>
          </div>
        </header>

        <div className="py-20 max-w-[1280px] mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Category Sidebar */}
            <aside className="w-full lg:w-64 shrink-0">
              <div className="lg:sticky lg:top-32 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => { setActiveCategory(cat); setOpenId(null); }}
                    className={`flex-shrink-0 w-full flex items-center justify-between p-4 rounded-xl font-semibold text-sm transition-all border ${
                      activeCategory === cat
                        ? 'bg-white shadow-sm border-[#e2e8f0] border-l-4 border-l-[#00255a] text-[#00255a]'
                        : 'bg-white border-[#e2e8f0] text-[#44474f] hover:bg-[#e6e8ea]'
                    }`}
                  >
                    {cat}
                    <span className="material-symbols-outlined text-[20px]">chevron_right</span>
                  </button>
                ))}
              </div>
            </aside>

            {/* Accordion */}
            <section className="flex-grow space-y-4">
              {filtered.length === 0 ? (
                <p className="text-[#64748b] text-center py-12">No questions found for this search.</p>
              ) : (
                filtered.map((faq) => {
                  const isOpen = openId === faq.id;
                  return (
                    <div
                      key={faq.id}
                      className={`bg-white rounded-xl border border-[#e2e8f0] shadow-sm transition-all duration-300 ${isOpen ? 'border-l-4 border-l-[#ff7b7b]' : ''}`}
                    >
                      <button
                        onClick={() => setOpenId(isOpen ? null : faq.id)}
                        className="w-full flex items-center justify-between p-6 text-left"
                      >
                        <span className="text-lg font-semibold text-[#00255a] pr-4">{faq.question}</span>
                        <span className={`material-symbols-outlined text-[#00255a] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                          expand_more
                        </span>
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-8 text-[#44474f] leading-relaxed">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </section>
          </div>
        </div>

        {/* Still Have Questions Banner */}
        <section className="mb-20 px-6">
          <div
            className="max-w-[1280px] mx-auto rounded-3xl p-12 md:p-20 text-center text-white relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          >
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-6">Need help finding scholarships to study abroad?</h2>
              <p className="text-lg max-w-2xl mx-auto mb-10 opacity-90">
                Speak with our consultants about eligible courses, scholarship options and your study abroad application.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <a
                  href="https://wa.me/263775827371"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 bg-white text-[#00255a] px-8 py-4 rounded-full font-bold shadow-xl hover:scale-105 transition-all"
                >
                  Chat with a Study Abroad Expert
                </a>
                <a href="/contact" className="px-8 py-4 rounded-full border-2 border-white/40 hover:border-white transition-all font-semibold">
                  Email Support
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
